import { useEffect } from 'react';
import {
  BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Navigate,
} from 'react-router';
import { useAuthStore } from '../store/authStore';
import { useBookingStore } from '../store/bookingStore';
import OnboardingScreen from './components/OnboardingScreen';
import LoginScreen from './components/LoginScreen';
import IdentityVerificationScreen from './components/IdentityVerificationScreen';
import AdminVerificationsScreen from './components/AdminVerificationsScreen';
import HomeScreen from './components/HomeScreen';
import ProductDetailScreen from './components/ProductDetailScreen';
import BookingScreen from './components/BookingScreen';
import CheckoutScreen from './components/CheckoutScreen';
import CourtsScreen from './components/CourtsScreen';
import CourtDetailScreen from './components/CourtDetailScreen';
import CourtCheckoutScreen from './components/CourtCheckoutScreen';
import CoachesScreen from './components/CoachesScreen';
import CoachDetailScreen from './components/CoachDetailScreen';
import BookCoachScreen from './components/BookCoachScreen';
import ClubsScreen from './components/ClubsScreen';
import ClubDetailScreen from './components/ClubDetailScreen';
import ShopScreen from './components/ShopScreen';
import ShopCollectionScreen from './components/ShopCollectionScreen';
import ShopProductScreen from './components/ShopProductScreen';
import ShopCartScreen from './components/ShopCartScreen';
import FavoritesScreen from './components/FavoritesScreen';
import CoachCheckoutScreen from './components/CoachCheckoutScreen';
import PackageCheckoutScreen from './components/PackageCheckoutScreen';
import OrdersScreen from './components/OrdersScreen';
import ProfileScreen from './components/ProfileScreen';
import PersonalInfoScreen from './components/PersonalInfoScreen';
import SavedAddressesScreen from './components/SavedAddressesScreen';
import HelpSupportScreen from './components/HelpSupportScreen';
import NotificationsScreen from './components/NotificationsScreen';
import Navigation from './components/Navigation';
import { screenToPath, pathToScreen, type Screen, type NavParams } from './routing';
import { identify, resetIdentity, trackPageView } from '../lib/analytics';

/**
 * Root of the customer app. Slice P1 #1 swapped the in-memory
 * `currentScreen` engine for React Router. URLs are now bookmarkable
 * (`/clubs/:id`, `/coaches/:id`, etc), browser back works, and
 * deferred deep links can wire in later.
 *
 * Screens still receive an `onNavigate(screen, params)` callback —
 * we adapt their existing prop signatures via the `useScreenNavigate`
 * hook below rather than rewriting every call site.
 */

type CourtBookingPayload = {
  courtId: string;
  startAt: string;
  endAt: string;
  clubId: string;
  rentals?: Array<{ rentalItemId: string; quantity: number }>;
};
type CoachBookingPayload = {
  coachId: string;
  lessonType: 'private' | 'semi_private' | 'group';
  environment: 'indoor' | 'outdoor';
  sessionMinutes: number;
  scheduledAt: string;
  participants: number;
  sessionCreditId?: string;
  courtId?: string;
  clubId?: string;
};

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

function Shell() {
  const { isAuthenticated, isLoading, user, profile } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const screen = pathToScreen(location.pathname);

  // First-run gating: if loading, sit on splash. If authed, route to
  // home. If not authed and not on a pre-auth screen, send to login.
  useEffect(() => {
    if (isLoading) return;
    const preAuth = ['onboarding', 'login', 'splash'].includes(screen);
    if (isAuthenticated && screen === 'splash') {
      navigate('/', { replace: true });
    } else if (!isAuthenticated && !preAuth) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, screen, navigate]);

  // PostHog: identify on auth, reset on sign-out. Wired in the Shell
  // (rather than the auth store) so the analytics layer stays the
  // store's consumer instead of a hidden cross-cutting dependency.
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated && user?.id) {
      identify(user.id, {
        email: user.email,
        name: profile?.full_name ?? undefined,
        identity_verified: !!profile?.identity_verified_at,
      });
    } else if (!isAuthenticated) {
      resetIdentity();
    }
  }, [isAuthenticated, isLoading, user?.id, user?.email, profile?.full_name, profile?.identity_verified_at]);

  // Track page views as the URL changes. Use the logical screen name
  // from the IA so routes with ids (`/coaches/:id`) collapse to one
  // event instead of fanning out into thousands of "unique pages".
  useEffect(() => {
    trackPageView(screen, { path: location.pathname });
  }, [screen, location.pathname]);

  if (isLoading && screen === 'splash') {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🎾</div>
          <div className="text-sm text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  const showNavigation = isAuthenticated && [
    'home', 'clubs', 'club', 'courts', 'court', 'coaches', 'coach', 'book-coach',
    'shop', 'shop-collection', 'shop-product', 'shop-cart',
    'orders', 'favorites', 'profile',
  ].includes(screen);

  return (
    <div className="w-full h-screen bg-background overflow-hidden flex flex-col">
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          {/* Pre-auth */}
          <Route path="/welcome" element={<OnboardingRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/identity-verification" element={<IdentityVerificationRoute />} />
          {/* Legacy machine-rental flow */}
          <Route path="/rent" element={<ProductRoute />} />
          <Route path="/rent/booking" element={<BookingRoute />} />
          <Route path="/rent/checkout" element={<CheckoutRoute />} />
          {/* Marketplace */}
          <Route path="/clubs" element={<ClubsRoute />} />
          <Route path="/clubs/:clubId" element={<ClubRoute />} />
          <Route path="/clubs/:clubId/venues/:venueId" element={<ClubRoute />} />
          <Route path="/courts" element={<CourtsRoute />} />
          <Route path="/courts/checkout" element={<CourtCheckoutRoute />} />
          <Route path="/courts/:courtId" element={<CourtRoute />} />
          <Route path="/coaches" element={<CoachesRoute />} />
          <Route path="/coaches/checkout" element={<CoachCheckoutRoute />} />
          <Route path="/coaches/package-checkout" element={<PackageCheckoutRoute />} />
          <Route path="/coaches/:coachId/book" element={<BookCoachRoute />} />
          <Route path="/coaches/:coachId" element={<CoachRoute />} />
          {/* Shop */}
          <Route path="/shop" element={<ShopRoute />} />
          <Route path="/shop/c/:slug" element={<ShopCollectionRoute />} />
          <Route path="/shop/p/:slug" element={<ShopProductRoute />} />
          <Route path="/shop/cart" element={<ShopCartRoute />} />
          {/* Activity */}
          <Route path="/bookings" element={<OrdersRoute />} />
          {/* Profile */}
          <Route path="/profile" element={<ProfileRoute />} />
          <Route path="/profile/favorites" element={<FavoritesRoute />} />
          <Route path="/profile/personal" element={<PersonalInfoRoute />} />
          <Route path="/profile/addresses" element={<SavedAddressesRoute />} />
          <Route path="/profile/help" element={<HelpSupportRoute />} />
          <Route path="/profile/notifications" element={<NotificationsRoute />} />
          {/* Admin */}
          <Route path="/admin/verifications" element={<AdminVerificationsRoute />} />
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {showNavigation && (
        <Navigation
          currentScreen={screen}
          onNavigate={(s) => navigate(screenToPath(s as Screen))}
        />
      )}
    </div>
  );
}

/**
 * Bridge from the screen-based onNavigate API to react-router. Screens
 * call onNavigate('foo', { id }); this hook turns that into a real
 * URL push (or replace) under the hood. Booking payloads that don't
 * fit in a URL travel via location.state.
 */
function useScreenNavigate() {
  const navigate = useNavigate();
  return (screen: Screen, params?: NavParams) => {
    const path = screenToPath(screen, params);
    navigate(path, params?.state ? { state: params.state } : undefined);
  };
}

/* -------------------------------------------------------------------- */
/* Route wrappers — each one adapts a screen's existing prop signature.  */
/* -------------------------------------------------------------------- */

function HomeRoute() {
  const navigate = useScreenNavigate();
  return <HomeScreen onNavigate={navigate} />;
}

function OnboardingRoute() {
  const navigate = useScreenNavigate();
  return <OnboardingScreen onComplete={() => navigate('login')} onSkip={() => navigate('login')} />;
}

function LoginRoute() {
  const navigate = useScreenNavigate();
  return (
    <LoginScreen
      onLogin={() => navigate('home')}
      onSignupSuccess={() => navigate('identity-verification')}
      onGuestContinue={() => navigate('home')}
    />
  );
}

function IdentityVerificationRoute() {
  const location = useLocation();
  const returnPath = ((location.state as { returnTo?: string } | null)?.returnTo) ?? '/';
  const reactNavigate = useNavigate();
  // Variant inference: standalone when the user came from /profile,
  // onboarding otherwise.
  const variant = returnPath.startsWith('/profile') ? 'standalone' : 'onboarding';
  const back = () => reactNavigate(returnPath, { replace: true });
  return (
    <IdentityVerificationScreen
      variant={variant}
      onDone={back}
      onSkip={back}
    />
  );
}

function ProductRoute() {
  const navigate = useScreenNavigate();
  return <ProductDetailScreen onNavigate={navigate} onBook={() => navigate('booking')} />;
}

function BookingRoute() {
  const navigate = useScreenNavigate();
  return <BookingScreen onNavigate={navigate} onContinue={() => navigate('checkout')} />;
}

function CheckoutRoute() {
  const navigate = useScreenNavigate();
  const { resetBooking } = useBookingStore();
  return (
    <CheckoutScreen
      onNavigate={navigate}
      onConfirm={() => { resetBooking(); navigate('orders'); }}
    />
  );
}

function CourtsRoute() {
  const navigate = useScreenNavigate();
  return <CourtsScreen onNavigate={navigate} />;
}

function CourtRoute() {
  const navigate = useScreenNavigate();
  const reactNavigate = useNavigate();
  const { courtId } = useParams<{ courtId: string }>();
  if (!courtId) return <Navigate to="/courts" replace />;
  return (
    <CourtDetailScreen
      courtId={courtId}
      onNavigate={navigate}
      onBook={(payload) => reactNavigate('/courts/checkout', { state: { courtBookingPayload: payload } })}
    />
  );
}

function CourtCheckoutRoute() {
  const navigate = useScreenNavigate();
  const location = useLocation();
  const reactNavigate = useNavigate();
  const payload = (location.state as { courtBookingPayload?: CourtBookingPayload } | null)?.courtBookingPayload;
  if (!payload) return <Navigate to="/courts" replace />;
  return (
    <CourtCheckoutScreen
      booking={payload}
      onNavigate={navigate}
      onConfirmed={() => reactNavigate('/bookings', { replace: true })}
    />
  );
}

function CoachesRoute() {
  const navigate = useScreenNavigate();
  return <CoachesScreen onNavigate={navigate} />;
}

function CoachRoute() {
  const navigate = useScreenNavigate();
  const { coachId } = useParams<{ coachId: string }>();
  if (!coachId) return <Navigate to="/coaches" replace />;
  return <CoachDetailScreen coachId={coachId} onNavigate={navigate} />;
}

function BookCoachRoute() {
  const navigate = useScreenNavigate();
  const reactNavigate = useNavigate();
  const { coachId } = useParams<{ coachId: string }>();
  if (!coachId) return <Navigate to="/coaches" replace />;
  return (
    <BookCoachScreen
      coachId={coachId}
      onNavigate={navigate}
      onBook={(payload) =>
        reactNavigate('/coaches/checkout', { state: { coachBookingPayload: payload } })}
      onBuyPackage={(packageId) =>
        reactNavigate('/coaches/package-checkout', {
          state: { packageId, coachId },
        })}
    />
  );
}

function CoachCheckoutRoute() {
  const navigate = useScreenNavigate();
  const location = useLocation();
  const reactNavigate = useNavigate();
  const payload = (location.state as { coachBookingPayload?: CoachBookingPayload } | null)?.coachBookingPayload;
  if (!payload) return <Navigate to="/coaches" replace />;
  return (
    <CoachCheckoutScreen
      booking={payload}
      onNavigate={navigate}
      onConfirmed={() => reactNavigate('/bookings', { replace: true })}
    />
  );
}

function PackageCheckoutRoute() {
  const navigate = useScreenNavigate();
  const location = useLocation();
  const reactNavigate = useNavigate();
  const state = location.state as { packageId?: string; coachId?: string } | null;
  if (!state?.packageId || !state.coachId) return <Navigate to="/coaches" replace />;
  return (
    <PackageCheckoutScreen
      packageId={state.packageId}
      coachId={state.coachId}
      onNavigate={navigate}
      onPurchased={() => reactNavigate(`/coaches/${state.coachId}`, { replace: true })}
    />
  );
}

function ClubsRoute() {
  const navigate = useScreenNavigate();
  return <ClubsScreen onNavigate={navigate} />;
}

function ClubRoute() {
  const navigate = useScreenNavigate();
  const { clubId, venueId } = useParams<{ clubId: string; venueId?: string }>();
  if (!clubId) return <Navigate to="/clubs" replace />;
  return <ClubDetailScreen clubId={clubId} venueId={venueId ?? null} onNavigate={navigate} />;
}

function ShopRoute() {
  const navigate = useScreenNavigate();
  return <ShopScreen onNavigate={navigate} />;
}

function ShopCollectionRoute() {
  const navigate = useScreenNavigate();
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <Navigate to="/shop" replace />;
  return <ShopCollectionScreen collectionHandle={slug} onNavigate={navigate} />;
}

function ShopProductRoute() {
  const navigate = useScreenNavigate();
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <Navigate to="/shop" replace />;
  return <ShopProductScreen productHandle={slug} onNavigate={navigate} />;
}

function ShopCartRoute() {
  const navigate = useScreenNavigate();
  return <ShopCartScreen onNavigate={navigate} />;
}

function OrdersRoute() {
  const navigate = useScreenNavigate();
  return <OrdersScreen onNavigate={navigate} />;
}

function ProfileRoute() {
  const navigate = useScreenNavigate();
  const reactNavigate = useNavigate();
  const { signOut } = useAuthStore();
  return (
    <ProfileScreen
      onNavigate={navigate}
      onLogout={async () => { await signOut(); reactNavigate('/login', { replace: true }); }}
    />
  );
}

function FavoritesRoute()       { const n = useScreenNavigate(); return <FavoritesScreen onNavigate={n} />; }
function PersonalInfoRoute()    { const n = useScreenNavigate(); return <PersonalInfoScreen onNavigate={n} />; }
function SavedAddressesRoute()  { const n = useScreenNavigate(); return <SavedAddressesScreen onNavigate={n} />; }
function HelpSupportRoute()     { const n = useScreenNavigate(); return <HelpSupportScreen onNavigate={n} />; }
function NotificationsRoute()   { const n = useScreenNavigate(); return <NotificationsScreen onNavigate={n} />; }
function AdminVerificationsRoute() {
  const n = useScreenNavigate();
  return <AdminVerificationsScreen onNavigate={n} />;
}
